import { useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AlertCircle, MailCheck } from 'lucide-react'
import toast from 'react-hot-toast'
import { authApi } from '../api/auth'
import { PageSpinner } from '../components/ui/Spinner'

interface FormData { newPassword: string; confirmPassword: string }

export function AcceptInvitePage() {
  useEffect(() => { document.title = 'Accept Invitation' }, [])
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const validation = useQuery({
    queryKey: ['invite-token-validity', token],
    queryFn: () => authApi.validateInviteToken(token).then(r => r.data.valid),
    enabled: token.length >= 32,
    retry: false,
    staleTime: Infinity,
  })

  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormData>()

  const mutation = useMutation({
    mutationFn: (dto: FormData) => authApi.acceptInvite(token, dto.newPassword),
    onSuccess: () => {
      toast.success('Password set! You can now sign in.')
      navigate('/login', { replace: true })
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message ?? 'Invitation link is invalid or has expired.'
      toast.error(typeof msg === 'string' ? msg : 'Failed to accept invitation.')
    },
  })

  const newPassword = watch('newPassword')
  const tokenMissing = !token || token.length < 32
  const tokenInvalid = validation.isError || validation.data === false

  if (tokenMissing || tokenInvalid) return <InvalidInvitePanel />
  if (validation.isLoading) return <PageSpinner />

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-600 mb-4">
            <MailCheck className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">Set your password</h1>
          <p className="text-sm text-gray-500 mt-1">Choose a password to activate your account.</p>
        </div>

        <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="card p-6 space-y-4">
          <div>
            <label className="label">New password</label>
            <input
              {...register('newPassword', {
                required: 'Password is required',
                minLength: { value: 6, message: 'At least 6 characters' },
              })}
              type="password"
              autoComplete="new-password"
              className="input"
              placeholder="••••••••"
            />
            {errors.newPassword && (
              <p className="text-xs text-red-600 mt-1">{errors.newPassword.message}</p>
            )}
          </div>
          <div>
            <label className="label">Confirm password</label>
            <input
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: v => v === newPassword || 'Passwords do not match',
              })}
              type="password"
              autoComplete="new-password"
              className="input"
              placeholder="••••••••"
            />
            {errors.confirmPassword && (
              <p className="text-xs text-red-600 mt-1">{errors.confirmPassword.message}</p>
            )}
          </div>
          <button type="submit" disabled={mutation.isPending} className="btn-primary btn w-full">
            {mutation.isPending ? 'Activating...' : 'Activate account'}
          </button>
        </form>
      </div>
    </div>
  )
}

function InvalidInvitePanel() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-amber-500 mb-4">
            <AlertCircle className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">Invitation expired or invalid</h1>
          <p className="text-sm text-gray-500 mt-1">
            Invitation links expire after 48 hours and can only be used once.
          </p>
        </div>
        <div className="card p-6 space-y-3 text-center">
          <p className="text-sm text-gray-700">
            Ask your administrator to resend the invitation.
          </p>
          <Link to="/login" className="btn-primary btn w-full">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  )
}
